import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useStatusColors } from '../contexts/StatusColorsContext';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { socketService } from '@platform/socket';
import { authenticatedFetch } from '../services/authService';

interface Pompa {
  id: number | string;
  region_id: number;
  nazwa: string;
  rodzaj: string | null;
  zabudowa: string | null;
  marka: string | null;
  model: string | null;
  nrrej: string | null;
  kierowca: string | null;
  nrpompy: string | null;
  isVirtual?: boolean;
}

interface ZamowieniePompy {
  id: number;
  pump_id: number | null;
  klient_nazwa: string;
  klient_budowa: string;
  zamowienie_godzinaod: string;
  zamowienie_godzinado: string;
  zamowienie_dzien: string;
  zamowienie_ilosc: number;
  pompa_nazwa: string | null;
  pompa_kierowca: string | null;
  pompa_numer: string | null;
  status?: string | null;
  zamowienie_potwierdzone?: boolean | null;
  zamowienie_realizacja?: boolean | null;
  zamowienie_zakonczone?: boolean | null;
  zamowienie_anulowane?: boolean | null;
}

interface TypPrzestoju {
  id: number;
  nazwa: string;
}

interface Przestoj {
  id: number;
  pompa_id: number;
  typ_przestoju_id: number;
  dataod: string;
  datado: string;
}

interface PompyWidgetProps {
  selectedRegion: number | null;
  selectedDate: string;
  hideHeader?: boolean;
  timelineWindowHours?: PompyTimelinePreset;
}

const TIMELINE_START = 0;
const TIMELINE_DEFAULT_RANGE = 24;
export const POMPY_TIMELINE_PRESETS = [24, 16, 12] as const;
const TIMELINE_TOTAL_HOURS = TIMELINE_DEFAULT_RANGE;
export type PompyTimelinePreset = typeof POMPY_TIMELINE_PRESETS[number];

const PompyWidget: React.FC<PompyWidgetProps> = ({ selectedRegion, selectedDate, hideHeader = false, timelineWindowHours }) => {
  const statusColors = useStatusColors();
  const [pompy, setPompy] = useState<Pompa[]>([]);
  const [zamowienia, setZamowienia] = useState<ZamowieniePompy[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    pumpId: number | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    pumpId: null
  });
  const [downtimeModal, setDowntimeModal] = useState<{
    visible: boolean;
    pumpId: number | null;
    pumpName: string;
  }>({
    visible: false,
    pumpId: null,
    pumpName: ''
  });
  const [downtimeData, setDowntimeData] = useState<{
    typ_przestoju_id: number;
    godzinaod: string;
    godzinado: string;
    isCałodniowy: boolean;
    dataod: string;
    datado: string;
  }>({
    typ_przestoju_id: 0,
    godzinaod: '',
    godzinado: '',
    isCałodniowy: false,
    dataod: '',
    datado: ''
  });
  const [typPrzestojow, setTypPrzestojow] = useState<TypPrzestoju[]>([]);
  const [loadingTypPrzestojow, setLoadingTypPrzestojow] = useState(false);
  const [przestoj, setPrzestoj] = useState<Przestoj[]>([]);
  const [tooltip, setTooltip] = useState<{ visible: boolean; text: string; x: number; y: number }>({ visible: false, text: '', x: 0, y: 0 });
  const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const timelineWrapperRef = useRef<HTMLDivElement | null>(null);
  const [availableTimelineWidth, setAvailableTimelineWidth] = useState<number>(0);

  const effectiveTimelineWindowHours = timelineWindowHours ?? TIMELINE_DEFAULT_RANGE;
  const shrinkHoursPerSide = Math.max(0, (TIMELINE_TOTAL_HOURS - effectiveTimelineWindowHours) / 2);
  const visibleStartHour = TIMELINE_START + shrinkHoursPerSide;
  const visibleEndHour = visibleStartHour + effectiveTimelineWindowHours;

  const timelineStartMinutes = visibleStartHour * 60;
  const timelineEndMinutes = visibleEndHour * 60;

  const selectedDateRef = useRef<string>(selectedDate);
  const selectedRegionRef = useRef<number | null>(selectedRegion);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  useEffect(() => { selectedRegionRef.current = selectedRegion; }, [selectedRegion]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = viewportWidth <= 768;
  const BASE_ROW_HEIGHT = isMobile ? 18 : 20;
  const LANE_HEIGHT = Math.max(BASE_ROW_HEIGHT - 1, 1);
  const pumpColumnWidth = isMobile ? 140 : 170;
  const defaultHourWidth = isMobile ? 32 : 55;
  const minHourWidth = isMobile ? 18 : 28;

  useEffect(() => {
    const element = timelineWrapperRef.current;
    if (!element) {
      return;
    }
    const updateWidth = () => {
      setAvailableTimelineWidth(element.clientWidth);
    };
    updateWidth();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(element);
      return () => observer.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
    return;
  }, [effectiveTimelineWindowHours, loading]);

  const hourWidth = useMemo(() => {
    if (effectiveTimelineWindowHours <= 0) {
      return defaultHourWidth;
    }
    const measured = availableTimelineWidth > 0 ? (availableTimelineWidth - pumpColumnWidth) / effectiveTimelineWindowHours : 0;
    if (!Number.isFinite(measured) || measured <= 0) {
      return defaultHourWidth;
    }
    return Math.max(minHourWidth, measured);
  }, [availableTimelineWidth, pumpColumnWidth, effectiveTimelineWindowHours, defaultHourWidth, minHourWidth]);

  const totalTimelineWidth = pumpColumnWidth + hourWidth * (effectiveTimelineWindowHours > 0 ? effectiveTimelineWindowHours : 1);
  const wrapperWidthForOverflow = availableTimelineWidth > 0 ? availableTimelineWidth : totalTimelineWidth;

  const normalize = (value: string | null | undefined): string => (value || '').trim().toUpperCase();

  const loadTypPrzestojow = useCallback(async () => {
    try {
      setLoadingTypPrzestojow(true);
      const response = await authenticatedFetch('/api/admin/typy-przestojow', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const typyData = data.typyPrzestojow || [];
        setTypPrzestojow(typyData);
      }
    } catch (error) {
      console.error('Error loading typy przestojow:', error);
    } finally {
      setLoadingTypPrzestojow(false);
    }
  }, []);

  const loadPrzestoj = useCallback(async () => {
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (selectedRegion) {
        params.append('region_id', selectedRegion.toString());
      }
      const response = await authenticatedFetch(`/api/przestoj?${params.toString()}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const przestojData = data.przestoj || [];
        setPrzestoj(przestojData);
      }
    } catch (error) {
      console.error('Error loading przestoj:', error);
    }
  }, [selectedDate, selectedRegion]);

  // Load typy przestojow only after authentication is complete
  useAuthenticatedEffect(() => {
    loadTypPrzestojow();
  }, [loadTypPrzestojow]);

  // Load przestoj only after authentication is complete
  useAuthenticatedEffect(() => {
    if (selectedDate) {
      loadPrzestoj();
    }
  }, [selectedDate, loadPrzestoj]);

  const ensurePumpExistsForName = useCallback((pumpName: string | null | undefined) => {
    const name = (pumpName || '').trim();
    if (!name) return;
    const norm = normalize(name);
    const exists = pompy.some((pump: Pompa) => {
      const sameName = normalize(pump.nazwa) === norm;
      const sameNumber = pump.nrpompy && normalize(pump.nrpompy) === norm;
      return sameName || Boolean(sameNumber);
    });
    if (exists) return;
    const virtualPump: Pompa = {
      id: `virtual_${Date.now()}_${Math.floor(Math.random()*1000)}`,
      region_id: (selectedRegion as number) || 0,
      nazwa: name,
      rodzaj: null,
      zabudowa: null,
      marka: null,
      model: null,
      nrrej: null,
      kierowca: null,
      nrpompy: null,
      isVirtual: true
    };
    setPompy(prev => [...prev, virtualPump]);
  }, [pompy, selectedRegion]);

  const loadScheduleData = useCallback(async () => {
    try {
      setLoading(true);
      const pumpsResponse = await authenticatedFetch(`/api/pompa${selectedRegionRef.current !== null ? `?region_id=${selectedRegionRef.current}` : ''}`, { credentials: 'include' });
      const ordersResponse = await authenticatedFetch(`/api/zamowienia/schedule?date=${selectedDateRef.current}${selectedRegionRef.current !== null ? `&region_id=${selectedRegionRef.current}` : ''}`, { credentials: 'include' });

      if (pumpsResponse.ok && ordersResponse.ok) {
        const pumpsData = await pumpsResponse.json();
        const ordersData = await ordersResponse.json();
        const basePumps = pumpsData.pumps || [];
        const orders = ordersData.orders || [];

        const orderPumpNameSet = new Set(
          orders
            .filter((order: ZamowieniePompy) => order.pompa_nazwa && order.pompa_nazwa !== "")
            .map((order: ZamowieniePompy) => normalize(order.pompa_nazwa))
        );

        const basePumpNameSet = new Set(basePumps.map((pump: Pompa) => normalize(pump.nazwa)));

        const missingPumpNamesNorm = Array.from(orderPumpNameSet).filter(name => !basePumpNameSet.has(name));

        const virtualPumps = missingPumpNamesNorm.map((normName, index) => {
          const orderForName = orders.find((o: ZamowieniePompy) => normalize(o.pompa_nazwa) === normName);
          const displayName = orderForName?.pompa_nazwa || normName;
          return {
            id: `virtual_${index}`,
            region_id: selectedRegion as number,
            nazwa: displayName,
            rodzaj: null,
            zabudowa: null,
            marka: null,
            model: null,
            nrrej: null,
            kierowca: null,
            nrpompy: null,
            isVirtual: true
          } as Pompa;
        });
        
        const allPumps = [...basePumps, ...virtualPumps];
        setPompy(allPumps);
        setZamowienia(orders);
      }
    } catch (error) {
      console.error('Error loading schedule data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedRegion]);

  useEffect(() => {
    if (selectedRegion !== null) {
      loadScheduleData();
    }
  }, [selectedRegion, selectedDate, loadScheduleData]);

  useEffect(() => {
    const initPromise = socketService.init();
    Promise.resolve(initPromise).catch(err => {
      console.error('[PompyWidget] Failed to initialize socket:', err);
    });

    const handler = (evt: any) => {
      try {
        if (!evt) return;
        if (evt.table && typeof evt.table === 'string' && evt.table.includes('_p')) return;
        const toYMDps = (v: any) => { try { return String(v).slice(0,10); } catch { return undefined; } };
        const evDatePs = toYMDps(evt.row?.zamowienie_dzien || evt.zamowienie_dzien || evt.old_row?.zamowienie_dzien);
        const evRegionRawPs = (evt.row?.region_id ?? evt.region_id ?? evt.old_row?.region_id);
        const evRegionPs = (evRegionRawPs === null || evRegionRawPs === undefined) ? undefined : Number(evRegionRawPs);
        const dateMatches = Boolean(evDatePs && selectedDateRef.current && evDatePs === selectedDateRef.current);
        const regionMatches = Boolean(evRegionPs !== undefined && selectedRegionRef.current !== null && Number(evRegionPs) === Number(selectedRegionRef.current));
        if (!dateMatches || !regionMatches) {
          return;
        }
        if (evt.table === 'przestoj') {
          loadPrzestoj();
          return;
        }
        if (evt.table === 'pompa') {
          loadScheduleData();
          return;
        }
        if (evt.table !== 'zamowienie') return;
        const evDate2 = (evt.row?.zamowienie_dzien || evt.zamowienie_dzien || evt.old_row?.zamowienie_dzien);
        const toYMD2 = (v: any) => { try { return String(v).slice(0,10); } catch { return null; } };
        const matchDate = !evDate2 || toYMD2(evDate2) === selectedDateRef.current;
        const evRegion2 = (evt.row?.region_id ?? evt.region_id ?? evt.old_row?.region_id ?? null);
        const matchRegion = (selectedRegionRef.current === null) || (evRegion2 === null) || (Number(evRegion2) === Number(selectedRegionRef.current));
        const idNum = Number(
          (evt && (evt.id as any)) ??
          (evt && evt.row && ((evt.row as any).id ?? (evt.row as any).zamowienie_id)) ??
          (evt && evt.old_row && ((evt.old_row as any).id ?? (evt.old_row as any).zamowienie_id))
        );
        if (!matchDate || !matchRegion) {
          if (idNum) setZamowienia(prev => prev.filter(o => Number(o.id) !== idNum));
          return;
        }

        const changedKeys: string[] = Array.isArray(evt.changed)
          ? evt.changed
          : (evt.changed && typeof evt.changed === 'object')
            ? Object.keys(evt.changed)
            : [];
        if (changedKeys.length === 0 && evt.row && evt.old_row) {
          const keys = new Set<string>([...Object.keys(evt.row), ...Object.keys(evt.old_row)]);
          keys.forEach(k => { if (evt.row[k] !== evt.old_row[k]) changedKeys.push(k); });
        }

        const op = String(evt.operation || '').toUpperCase();
        if (op === 'INSERT') {
          const row = evt.row || null;
          if (row) {
            ensurePumpExistsForName(row.pompa_nazwa);
            setZamowienia(prev => {
              if (prev.some(o => o.id === row.id)) return prev;
              return [...prev, row as ZamowieniePompy];
            });
          }
          return;
        }
        if (op === 'UPDATE') {
          const pumpChanged = changedKeys.some(k => k === 'pompa_nazwa' || k === 'pompa_numer' || k === 'pompa_kierowca');
          const timeChanged = changedKeys.some(k => k === 'zamowienie_godzinaod' || k === 'zamowienie_godzinado');
          const labelChanged = changedKeys.some(k => k === 'zamowienie_ilosc' || k === 'klient_nazwa' || k === 'klient_budowa');
          if (pumpChanged || timeChanged || labelChanged) {
            const row = evt.row || {};
            if (pumpChanged) ensurePumpExistsForName(row.pompa_nazwa);
            setZamowienia(prev => prev.map(o => (o.id === idNum ? ({ ...o, ...row }) : o)));
            return;
          }
          let statusChanged = changedKeys.some(k => (
            k === 'status' || k === 'zamowienie_potwierdzone' || k === 'zamowienie_realizacja' ||
            k === 'zamowienie_zakonczone' || k === 'zamowienie_anulowane'
          ));
          if (!statusChanged && evt.row && evt.old_row) {
            const fields = ['status','zamowienie_potwierdzone','zamowienie_realizacja','zamowienie_zakonczone','zamowienie_anulowane'] as const;
            statusChanged = fields.some((f) => (evt.row?.[f] !== evt.old_row?.[f]));
          }
          if (statusChanged) {
            const row = evt.row || {};
            setZamowienia(prev => prev.map(o => (o.id === idNum ? ({ ...o, ...row }) : o)));
            return;
          }
          return;
        }
        if (op === 'DELETE') {
          if (idNum) setZamowienia(prev => prev.filter(o => Number(o.id) !== idNum));
          return;
        }
      } catch {}
    };
    
    const unsubscribe = socketService.subscribe(handler);
    window.addEventListener('db:change', handler as EventListener);
    return () => {
      unsubscribe();
      window.removeEventListener('db:change', handler as EventListener);
    };
  }, [loadScheduleData, ensurePumpExistsForName, loadPrzestoj]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (
        (!detail.date || detail.date === selectedDateRef.current) &&
        (!detail.region_id || detail.region_id === selectedRegionRef.current)
      ) {
        loadScheduleData();
      }
    };
    window.addEventListener('pumpSchedule:refresh', handler as EventListener);
    return () => window.removeEventListener('pumpSchedule:refresh', handler as EventListener);
  }, [loadScheduleData]);

  useEffect(() => {
    const onOrderUpdate = (e: Event) => {
      try {
        const row = (e as CustomEvent).detail?.row;
        if (!row) return;
        ensurePumpExistsForName(row.pompa_nazwa);
        setZamowienia(prev => prev.map(o => (o.id === row.id ? ({ ...o, ...row }) : o)));
      } catch {}
    };
    window.addEventListener('pumpSchedule:orderUpdate', onOrderUpdate as EventListener);
    return () => window.removeEventListener('pumpSchedule:orderUpdate', onOrderUpdate as EventListener);
  }, [ensurePumpExistsForName]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        handleContextMenuClose();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);

  const handleRowClick = (pumpId: number) => {
    setSelectedRowId(pumpId);
  };

  const handleContextMenu = (e: React.MouseEvent, pumpId: number) => {
    e.preventDefault();
    const pump = pompy.find(p => p.id === pumpId);
    if (!pump || pump.isVirtual) return;
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      pumpId
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      pumpId: null
    });
  };

  const handleAddDowntime = () => {
    const pump = pompy.find(p => p.id === contextMenu.pumpId);
    if (!pump) return;
    setDowntimeModal({
      visible: true,
      pumpId: contextMenu.pumpId,
      pumpName: pump.nazwa
    });
    setDowntimeData({
      typ_przestoju_id: 0,
      godzinaod: '08:00',
      godzinado: '10:00',
      isCałodniowy: false,
      dataod: selectedDate,
      datado: selectedDate
    });
    handleContextMenuClose();
  };

  const handleSaveDowntime = async () => {
    if (!downtimeModal.pumpId || !downtimeData.typ_przestoju_id) {
      alert('Proszę wybrać typ przestoju');
      return;
    }

    try {
      let dataod, datado;
      if (downtimeData.isCałodniowy) {
        const od = (downtimeData.dataod || selectedDate);
        const doD = (downtimeData.datado || downtimeData.dataod || selectedDate);
        dataod = `${od}T00:00:00`;
        datado = `${doD}T23:59:00`;
      } else {
        dataod = `${selectedDate}T${downtimeData.godzinaod}:00`;
        datado = `${selectedDate}T${downtimeData.godzinado}:00`;
      }

      const response = await authenticatedFetch('/api/przestoj', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pompa_id: downtimeModal.pumpId,
          typ_przestoju_id: downtimeData.typ_przestoju_id,
          dataod,
          datado
        })
      });

      if (response.ok) {
        setDowntimeModal({
          visible: false,
          pumpId: null,
          pumpName: ''
        });
        setDowntimeData({
          typ_przestoju_id: 0,
          godzinaod: '',
          godzinado: '',
          isCałodniowy: false,
          dataod: '',
          datado: ''
        });
        await loadPrzestoj();
      } else {
        const errorData = await response.json();
        alert(`Błąd podczas dodawania przestoju: ${errorData.message || 'Nieznany błąd'}`);
      }
    } catch (error) {
      console.error('Error saving downtime:', error);
      alert('Błąd podczas dodawania przestoju');
    }
  };

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const extractDateFromDateTime = (dt: string): string => {
    try { return (dt || '').split('T')[0] || ''; } catch { return ''; }
  };

  const parseLocalHMFromDateTime = (dt: string): { hours: number; minutes: number } => {
    try {
      const timePart = (dt || '').split('T')[1] || '';
      const hhmm = timePart.split('.')[0] || timePart;
      const [hh, mm] = (hhmm || '').split(':');
      const hours = parseInt(hh || '0', 10) || 0;
      const minutes = parseInt(mm || '0', 10) || 0;
      return { hours, minutes };
    } catch { return { hours: 0, minutes: 0 }; }
  };

  const minutesSinceMidnightFromDateTime = (dt: string): number => {
    const { hours, minutes } = parseLocalHMFromDateTime(dt);
    return hours * 60 + minutes;
  };

  const isFullDayRange = (from: string, to: string): boolean => {
    const startDate = extractDateFromDateTime(from);
    const endDate = extractDateFromDateTime(to);
    const s = parseLocalHMFromDateTime(from);
    const e = parseLocalHMFromDateTime(to);
    return (
      startDate !== endDate ||
      ((s.hours === 0 && s.minutes === 0) && ((e.hours === 23 && e.minutes >= 59) || (e.hours === 24 && e.minutes === 0)))
    );
  };

  const layoutPumpOrders = (ordersForPump: ZamowieniePompy[]) => {
    const filtered = [...ordersForPump]
      .filter(order => {
        const start = timeToMinutes(order.zamowienie_godzinaod);
        const end = timeToMinutes(order.zamowienie_godzinado);
        return end > timelineStartMinutes && start < timelineEndMinutes;
      })
      .sort((a, b) => {
        const startA = Math.max(timeToMinutes(a.zamowienie_godzinaod), timelineStartMinutes);
        const startB = Math.max(timeToMinutes(b.zamowienie_godzinaod), timelineStartMinutes);
        return startA - startB;
      });
    const laneEnds: number[] = [];
    const placed = filtered.reduce<Array<{ order: ZamowieniePompy; lane: number }>>((acc, order) => {
      const start = Math.max(timeToMinutes(order.zamowienie_godzinaod), timelineStartMinutes);
      const end = Math.min(timeToMinutes(order.zamowienie_godzinado), timelineEndMinutes);
      if (end <= start) {
        return acc;
      }
      let lane = laneEnds.findIndex(lEnd => lEnd <= start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(end);
      } else {
        laneEnds[lane] = end;
      }
      acc.push({ order, lane });
      return acc;
    }, []);
    return { placed, lanes: laneEnds.length };
  };

  const getOrderColor = (order: any): { bg: string; border: string; text: string; hoverBg: string; hoverBorder: string; hoverText: string } => {
    const sc = statusColors;
    const potwierdzone = Boolean(order.zamowienie_potwierdzone);
    const realizacja = Boolean(order.zamowienie_realizacja);
    const zakonczone = Boolean(order.zamowienie_zakonczone);
    const anulowane = Boolean(order.zamowienie_anulowane);

    if (anulowane) return { bg: sc.Anulowane.bg, border: sc.Anulowane.border, text: sc.Anulowane.text, hoverBg: sc.Anulowane.hoverBg, hoverBorder: sc.Anulowane.border, hoverText: sc.Anulowane.text };
    if (zakonczone) return { bg: sc.Zakonczone.bg, border: sc.Zakonczone.border, text: sc.Zakonczone.text, hoverBg: sc.Zakonczone.hoverBg, hoverBorder: sc.Zakonczone.border, hoverText: sc.Zakonczone.text };
    if (realizacja) return { bg: sc.Realizacja.bg, border: sc.Realizacja.border, text: sc.Realizacja.text, hoverBg: sc.Realizacja.hoverBg, hoverBorder: sc.Realizacja.border, hoverText: sc.Realizacja.text };
    if (potwierdzone) return { bg: sc.Potwierdzone.bg, border: sc.Potwierdzone.border, text: sc.Potwierdzone.text, hoverBg: sc.Potwierdzone.hoverBg, hoverBorder: sc.Potwierdzone.border, hoverText: sc.Potwierdzone.text };
    return { bg: '#ffffff', border: '#1976D2', text: '#1976D2', hoverBg: 'rgba(25,118,210,0.10)', hoverBorder: '#1565C0', hoverText: '#1565C0' };
  };

  const calculateOrderStyle = (order: ZamowieniePompy): React.CSSProperties | null => {
    const startMinutesRaw = timeToMinutes(order.zamowienie_godzinaod);
    const endMinutesRaw = timeToMinutes(order.zamowienie_godzinado);
    const visibleStart = Math.max(startMinutesRaw, timelineStartMinutes);
    const visibleEnd = Math.min(endMinutesRaw, timelineEndMinutes);
    if (visibleEnd <= visibleStart) {
      return null;
    }
    const duration = visibleEnd - visibleStart;
    const c = getOrderColor(order as any);

    return {
      left: `${((visibleStart - timelineStartMinutes) / 60) * hourWidth}px`,
      width: `${(duration / 60) * hourWidth}px`,
      height: `${Math.max(LANE_HEIGHT - 2, 2)}px`,
      minWidth: 0,
      position: 'absolute' as const,
      backgroundColor: c.bg,
      borderRadius: '2px',
      color: (c as any).text || 'white',
      fontSize: '10px',
      padding: '0px 1px',
      overflow: 'hidden',
      whiteSpace: 'nowrap' as const,
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'center',
      lineHeight: '1.2',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: `1px solid ${c.border}`,
      zIndex: 1
    };
  };

  const calculateDowntimeStyle = (przestoj: Przestoj): React.CSSProperties | null => {
    const startDateStr = extractDateFromDateTime(przestoj.dataod);
    const endDateStr = extractDateFromDateTime(przestoj.datado);
    const fullDay = isFullDayRange(przestoj.dataod, przestoj.datado);

    const startMinutesRaw = minutesSinceMidnightFromDateTime(przestoj.dataod);
    const endMinutesRaw = minutesSinceMidnightFromDateTime(przestoj.datado);
    const visibleStart = Math.max(startMinutesRaw, timelineStartMinutes);
    const visibleEnd = Math.min(endMinutesRaw, timelineEndMinutes);
    if (visibleEnd <= visibleStart) {
      return null;
    }
    const duration = Math.max(0, visibleEnd - visibleStart);

    return {
      left: `${((visibleStart - timelineStartMinutes) / 60) * hourWidth}px`,
      width: `${(duration / 60) * hourWidth}px`,
      height: `${Math.max(LANE_HEIGHT - 2, 2)}px`,
      minWidth: 0,
      position: 'absolute' as const,
      backgroundColor: fullDay ? '#ef9a9a' : '#ef9a9a',
      borderRadius: '3px',
      color: 'white',
      fontSize: '10px',
      padding: '0px 1px',
      overflow: 'hidden',
      whiteSpace: 'nowrap' as const,
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '1.2',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: `1px solid #e57373`,
      zIndex: 2,
      fontWeight: 'bold',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
    };
  };

  const timelineHours = useMemo(
    () => Array.from({ length: effectiveTimelineWindowHours }, (_, i) => visibleStartHour + i),
    [effectiveTimelineWindowHours, visibleStartHour]
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        border: hideHeader ? 'none' : '1px solid var(--border)',
        borderRadius: hideHeader ? '0' : '4px',
        background: 'var(--card)',
        minWidth: 0,
        overflow: 'visible',
        minHeight: hideHeader ? 'auto' : 'auto',
        height: 'auto'
      }}>
        {!hideHeader && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 10px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg)'
          }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Harmonogram Pomp</span>
          </div>
        )}
        <div style={{ padding: hideHeader ? '0' : '8px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 0 }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'center', padding: '20px' }}>Ładowanie harmonogramu...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0px',
      border: hideHeader ? 'none' : '1px solid var(--border)',
      borderRadius: hideHeader ? '0' : '4px',
      background: 'var(--card)',
      minWidth: 0,
      overflow: 'visible',
      minHeight: 'auto',
      height: 'auto'
    }}>
      {!hideHeader && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 10px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg)'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Harmonogram Pomp</span>
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            {pompy.length} pomp, {zamowienia.length} zamówień, {przestoj.length} przestojów
          </span>
        </div>
      )}
      <div style={{ padding: hideHeader ? '0' : '8px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 0 }}>
        <div
          ref={timelineWrapperRef}
          style={{ 
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: totalTimelineWidth > wrapperWidthForOverflow ? 'auto' : 'hidden',
            overflowY: 'visible',
            minHeight: 0
          }}
        >
          <div style={{ width: `${totalTimelineWidth}px`, minWidth: `${totalTimelineWidth}px` }}>
            {/* Timeline header */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: `${pumpColumnWidth}px repeat(${effectiveTimelineWindowHours}, ${hourWidth}px)`,
              position: 'sticky',
              top: 0,
              backgroundColor: '#e8eaed',
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0'
            }}>
              <div style={{ padding: '1px', fontWeight: 500, fontSize: '10px', height: `${LANE_HEIGHT}px`, display: 'flex', alignItems: 'center' }}>Pompa</div>
              {timelineHours.map(hour => (
                <div
                  key={hour}
                  style={{
                    padding: '1px',
                    textAlign: 'center',
                    borderLeft: '1px solid var(--border)',
                    fontSize: '10px',
                    fontWeight: 500,
                    height: `${LANE_HEIGHT}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Pump rows */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {pompy.length === 0 ? (
                <div style={{ 
                  padding: '1px', 
                  textAlign: 'center', 
                  color: 'var(--muted)',
                  fontSize: '10px'
                }}>
                  Brak pomp w wybranym regionie
                </div>
              ) : (
                pompy.map(pump => {
                  const pumpOrdersRaw = zamowienia
                    .filter(order => normalize(order.pompa_nazwa) === normalize(pump.nazwa))
                    .filter(order => {
                      const statusStr = ((order as any).status || '').toString().toLowerCase();
                      return !(Boolean((order as any).zamowienie_anulowane) || statusStr.includes('anul'));
                    });
                  const { placed, lanes } = layoutPumpOrders(pumpOrdersRaw);
                  const rowHeightPx = Math.max(LANE_HEIGHT, lanes * LANE_HEIGHT);
                  const hasConflict = lanes > 1;

                  return (
                    <div
                      key={pump.id}
                      style={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: `${pumpColumnWidth}px repeat(${effectiveTimelineWindowHours}, ${hourWidth}px)`,
                        borderBottom: '1px solid var(--border)',
                        minHeight: `${rowHeightPx}px`,
                        backgroundColor: selectedRowId === pump.id ? '#e3f2fd' : 'transparent',
                        cursor: pump.isVirtual ? 'default' : 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={() => !pump.isVirtual && handleRowClick(pump.id as number)}
                      onContextMenu={(e) => !pump.isVirtual && handleContextMenu(e, pump.id as number)}
                    >
                      {/* Pump info */}
                      <div style={{
                        padding: '0px 1px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.2'
                      }}>
                        <div style={{ 
                          fontWeight: 500,
                          color: pump.isVirtual ? '#d32f2f' : '#212121',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1
                        }}>
                          {pump.nazwa}
                          {pump.nrpompy && ` (${pump.nrpompy})`}
                          {pump.kierowca && ` • ${pump.kierowca}`}
                        </div>
                        {hasConflict && (
                          <span
                            title={`Wykryto konflikty: ${lanes} pasy`}
                            style={{
                              flexShrink: 0,
                              marginLeft: 6,
                              width: 18,
                              height: 18,
                              borderRadius: 9,
                              backgroundColor: '#ef5350',
                              color: '#fff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1,
                              border: '1px solid #d32f2f',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                            }}
                          >
                            {lanes}
                          </span>
                        )}
                      </div>

                      {/* Timeline cells */}
                      {timelineHours.map(hour => (
                        <div
                          key={hour}
                          style={{
                            borderLeft: '1px solid var(--border)',
                            height: '100%'
                          }}
                        />
                      ))}

                      {/* Order blocks */}
                      <div style={{ position: 'absolute', left: `${pumpColumnWidth}px`, right: 0, height: '100%', minWidth: 0 }}>
                        {placed.map(({ order, lane }) => {
                          const style = calculateOrderStyle(order);
                          if (!style) {
                            return null;
                          }
                          const c = getOrderColor(order as any);
                          return (
                            <div
                              key={order.id}
                              style={{ ...style, top: `${lane * LANE_HEIGHT + 1}px` }}
                              title={`${order.klient_nazwa}\nBudowa: ${order.klient_budowa || ''}\n${order.zamowienie_godzinaod?.split(':').slice(0, 2).join(':') || ''}-${order.zamowienie_godzinado?.split(':').slice(0, 2).join(':') || ''}\n${order.zamowienie_ilosc}m³`}
                              onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = c.hoverBg;
                                e.currentTarget.style.borderColor = c.hoverBorder;
                                (e.currentTarget as HTMLElement).style.color = (c as any).hoverText || (c as any).text || 'white';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = c.bg;
                                e.currentTarget.style.borderColor = c.border;
                                (e.currentTarget as HTMLElement).style.color = (c as any).text || 'white';
                              }}
                            >
                              <span
                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1, padding: '0px' }}
                                onMouseEnter={(e) => {
                                  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setTooltip({ visible: true, text: String(order.klient_nazwa || ''), x: r.left + r.width / 2, y: r.top - 8 });
                                }}
                                onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                              >
                                {order.klient_nazwa}
                              </span>
                              <span style={{ flexShrink: 0, marginLeft: 2, padding: '0px' }}>
                                • {order.zamowienie_ilosc}m³
                              </span>
                            </div>
                          );
                        })}
                        
                        {/* Downtime blocks */}
                        {(() => {
                          const pumpDowntimes = przestoj.filter(p => p.pompa_id === pump.id);
                          return pumpDowntimes.map(downtime => {
                            const downtimeType = typPrzestojow.find(t => t.id === downtime.typ_przestoju_id);
                            const typeName = downtimeType?.nazwa || 'Przestój';
                            const startDate = extractDateFromDateTime(downtime.dataod);
                            const endDate = extractDateFromDateTime(downtime.datado);
                            const currentDate = selectedDate;
                            
                            if (currentDate >= startDate && currentDate <= endDate) {
                              const style = calculateDowntimeStyle(downtime);
                              if (!style) {
                                return null;
                              }
                              return (
                                <div
                                  key={downtime.id}
                                  style={style}
                                  title={`${typeName}\n${startDate !== endDate ? `${startDate} - ${endDate}` : (downtime.dataod.split('T')[1] || '').slice(0,5)} - ${(downtime.datado.split('T')[1] || '').slice(0,5)}`}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, pumpId: pump.id as number });
                                    (window as any).__ps_selected_downtime_id__ = downtime.id;
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor = '#e57373';
                                    e.currentTarget.style.borderColor = '#ef5350';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor = '#ef9a9a';
                                    e.currentTarget.style.borderColor = '#e57373';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  {typeName.length > 8 ? typeName.substring(0, 8) + '...' : typeName}
                                </div>
                              );
                            }
                            return null;
                          }).filter(Boolean);
                        })()}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            minWidth: '150px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={handleAddDowntime} style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#333' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>Dodaj przestój</button>
          {typeof (window as any).__ps_selected_downtime_id__ === 'number' && (
            <button
              onClick={async () => {
                try {
                  const id = (window as any).__ps_selected_downtime_id__;
                  (window as any).__ps_selected_downtime_id__ = undefined;
                  const resp = await authenticatedFetch(`/api/przestoj/${id}`, { method: 'DELETE', credentials: 'include' });
                  if (resp.ok) {
                    handleContextMenuClose();
                    await loadPrzestoj();
                  } else {
                    const t = await resp.text();
                    alert(`Nie udało się usunąć przestoju: ${t}`);
                  }
                } catch (e) {
                  alert('Błąd podczas usuwania przestoju');
                }
              }}
              style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#d32f2f' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ffebee'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >Usuń przestój</button>
          )}
        </div>
      )}

      {/* Downtime Modal */}
      {downtimeModal.visible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001
          }}
          onClick={() => setDowntimeModal({ visible: false, pumpId: null, pumpName: '' })}
        >
          <div
            style={{
              backgroundColor: 'var(--card)',
              borderRadius: '8px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>
              Dodaj przestój - {downtimeModal.pumpName}
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Typ przestoju:
              </label>
              <select
                value={downtimeData.typ_przestoju_id}
                onChange={(e) => setDowntimeData(prev => ({
                  ...prev,
                  typ_przestoju_id: Number(e.target.value)
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={0}>Wybierz typ przestoju</option>
                {typPrzestojow.map(typ => (
                  <option key={typ.id} value={typ.id}>
                    {typ.nazwa}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                <input
                  type="checkbox"
                  checked={downtimeData.isCałodniowy}
                  onChange={(e) => setDowntimeData(prev => ({
                    ...prev,
                    isCałodniowy: e.target.checked,
                    dataod: e.target.checked ? (prev.dataod || selectedDate) : prev.dataod,
                    datado: e.target.checked ? (prev.datado || prev.dataod || selectedDate) : prev.datado
                  }))}
                  style={{ margin: 0 }}
                />
                Przestój całodniowy
              </label>
            </div>

            {downtimeData.isCałodniowy && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Data od:
                  </label>
                  <input
                    type="date"
                    value={downtimeData.dataod}
                    onChange={(e) => setDowntimeData(prev => ({
                      ...prev,
                      dataod: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Data do:
                  </label>
                  <input
                    type="date"
                    value={downtimeData.datado}
                    onChange={(e) => setDowntimeData(prev => ({
                      ...prev,
                      datado: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </>
            )}

            {!downtimeData.isCałodniowy && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Godzina od:
                  </label>
                  <input
                    type="time"
                    value={downtimeData.godzinaod}
                    onChange={(e) => setDowntimeData(prev => ({
                      ...prev,
                      godzinaod: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Godzina do:
                  </label>
                  <input
                    type="time"
                    value={downtimeData.godzinado}
                    onChange={(e) => setDowntimeData(prev => ({
                      ...prev,
                      godzinado: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setDowntimeModal({ visible: false, pumpId: null, pumpName: '' })}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={handleSaveDowntime}
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.y,
            left: tooltip.x,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'rgba(33, 33, 33, 0.95)',
            color: '#fff',
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 12,
            pointerEvents: 'none',
            zIndex: 2000,
            whiteSpace: 'nowrap',
            maxWidth: '60vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default PompyWidget;

